import client from './client';

export interface LoginRequest {
  username: string;
  password?: string;
  deviceId: string;
}

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

export interface User {
  id: number;
  username: string;
  email: string; // If available
  enabled: boolean;
  activePlanName?: string;
  expiryDate?: string;
  deviceLimit?: number;
  name?: string;
  phoneNumber?: string;
  phoneVerified?: boolean;
  companyName?: string;
  businessRegistrationNumber?: string;
  representativeName?: string;
  openingDate?: string;
  status?: 'ACTIVE' | 'WITHDRAWING' | 'WITHDRAWN';
  withdrawalRequestedAt?: string;
  createdAt?: string;
  lastLoginAt?: string;
}

export interface UpdateUserRequest {
  name: string;
  phoneNumber: string;
  companyName: string;
  businessRegistrationNumber: string;
  representativeName?: string;
  openingDate?: string;
}

export const updateUser = async (userId: number, data: UpdateUserRequest) => {
  const response = await client.put(`/admin/user/${userId}`, data);
  return response.data;
};

export const updateUserDeviceLimit = async (userId: number, deviceLimit: number) => {
  const response = await client.put(`/admin/user/${userId}/device-limit`, { deviceLimit });
  return response.data;
};

export interface Promotion {
  id: number;
  name: string;
  type: 'RELATIVE' | 'FIXED';
  durationDays?: number;
  fixedEndDate?: string; // ISO Date string
  limitBytes: number;
  description?: string;
  targetUserType?: 'GENERAL' | 'BUSINESS';
  userType?: 'GENERAL' | 'BUSINESS';
}

export interface Server {
  id: number;
  idServerIp: string;
  idServerKey: string;
  enabled: boolean;
}

// Authentication
export const login = async (data: LoginRequest) => {
  const response = await client.post<TokenResponse>('/admin/login', data);
  return response.data;
};

export const refreshToken = async (refreshTokenRequest: { refreshToken: string; deviceId: string }) => {
  const response = await client.post<TokenResponse>('/admin/refresh', refreshTokenRequest);
  return response.data;
};

// Users
export const getUsers = async (page: number = 0, size: number = 10, keyword: string = '', sort: string = 'id,desc') => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: sort
  });
  if (keyword) params.append('keyword', keyword);

  const response = await client.get<Page<User>>(`/admin/user?${params.toString()}`);
  return response.data;
};

export const grantPromotion = async (userId: number, promotionId: number) => {
  return await client.post('/admin/license/grant-promotion', { userId, promotionId });
};

export interface Plan {
  id: number;
  name: string;
  durationDays: number;
  price: number;
  description?: string;
  limitBytes?: number;
  userType?: 'GENERAL' | 'BUSINESS';
}

// ... existing code ...

export const getPlans = async (page: number = 0, size: number = 10) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString()
  });
  const response = await client.get<Page<Plan>>(`/admin/plan?${params.toString()}`);
  return response.data;
};

export const createPlan = async (data: Omit<Plan, 'id'>) => {
  const response = await client.post<Plan>('/admin/plan', data);
  return response.data;
};

export const updatePlan = async (id: number, data: Omit<Plan, 'id'>) => {
  const response = await client.put<Plan>(`/admin/plan/${id}`, data);
  return response.data;
};

export const deletePlan = async (id: number) => {
  return await client.delete(`/admin/plan/${id}`);
};

export const grantSubscription = async (userId: number, planId: number) => {
  return await client.post('/admin/license/grant-subscription', { userId, planId });
};

// Promotions
export const getPromotions = async (page: number = 0, size: number = 10) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString()
  });
  const response = await client.get<Page<Promotion>>(`/admin/promotion?${params.toString()}`);
  return response.data;
};

export const createPromotion = async (data: Omit<Promotion, 'id'>) => {
  const response = await client.post<Promotion>('/admin/promotion', data);
  return response.data;
};

export const updatePromotion = async (id: number, data: Omit<Promotion, 'id'>) => {
  const response = await client.put<Promotion>(`/admin/promotion/${id}`, data);
  return response.data;
};

export const deletePromotion = async (id: number) => {
  return await client.delete(`/admin/promotion/${id}`);
};

// Servers
export const getServers = async () => {
  const response = await client.get<Server[]>('/admin/server');
  return response.data;
};

export const createServer = async (data: Pick<Server, 'idServerIp' | 'idServerKey'>) => {
  const response = await client.post<Server>('/admin/server', data);
  return response.data;
};

export const updateServer = async (id: number, data: Pick<Server, 'idServerIp' | 'idServerKey'>) => {
  const response = await client.put<Server>(`/admin/server/${id}`, data);
  return response.data;
};

// User Devices
export interface UserDevice {
  id: number;
  username: string;
  onesupportId: string;
  lastActiveAt: string;
}

export interface Page<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
}

export const getUserDevices = async (page: number = 0, size: number = 10, userId?: number, keyword?: string) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: 'accessedAt,desc'
  });
  if (userId) params.append('userId', userId.toString());
  if (keyword) params.append('keyword', keyword);

  const response = await client.get<Page<UserDevice>>(`/admin/device?${params.toString()}`);
  return response.data;
};

// ... (User Devices code)

export const deleteServer = async (id: number) => {
  return await client.delete(`/admin/server/${id}`);
};

// Relay Traffic Logs
export interface RelayTrafficLog {
  username: string;
  sessionId: string;
  bytesSent: number;
  bytesReceived: number;
  durationSeconds: number;
  connType: string;
  createdAt: string;
}

export const getRelayTrafficLogs = async (page: number = 0, size: number = 20, username: string = '') => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: 'createdAt,desc'
  });
  if (username) {
    params.append('username', username);
  }
  const response = await client.get<Page<RelayTrafficLog>>(`/logs/relay-traffic?${params.toString()}`);
  return response.data;
};

export interface RelayTrafficStats {
  username: string;
  weeklyBytesSent: number;
  monthlyBytesSent: number;
}

export const getRelayTrafficStats = async (username: string) => {
  const response = await client.get<RelayTrafficStats>(`/logs/relay-traffic/stats?username=${username}`);
  return response.data;
};

// Payment History
export interface PaymentHistory {
  id: number;
  username: string;
  merchantUid: string;
  impUid?: string;
  amount: number;
  status: string;
  paymentType: string;
  paymentDate: string;
  failureReason?: string;
  cancelledAt?: string;
  failedAt?: string;
  planName?: string;
  planId?: number;
  relatedSubscriptionId?: number;
}

export const getPaymentHistory = async (page: number = 0, size: number = 20, status: string = '') => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: 'paymentDate,desc'
  });
  if (status && status !== 'ALL') {
    params.append('status', status);
  }
  const response = await client.get<Page<PaymentHistory>>(`/admin/payment?${params.toString()}`);
  return response.data;
};

export interface PaymentSchedule {
  id: number;
  userId: number;
  username: string;
  planName: string;
  amount: number;
  merchantUid: string;
  scheduledAt: string;
  status: string;
  processedAt?: string;
  createdAt: string;
  cancelledAt?: string;
  failedAt?: string;
}

export const getPaymentSchedules = async (page: number = 0, size: number = 20, status: string = '') => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: 'scheduledAt,desc'
  });
  if (status && status !== 'ALL') {
    params.append('status', status);
  }
  const response = await client.get<Page<PaymentSchedule>>(`/admin/schedule?${params.toString()}`);
  return response.data;
};

export const cancelPaymentSchedule = async (id: number) => {
  return await client.post(`/admin/schedule/${id}/cancel`);
};

// User Subscription History
export interface UserSubscriptionHistory {
  id: number;
  startDate: string;
  endDate: string;
  enabled: boolean;
  disabledAt?: string;
  sourceType: string;
  sourceName: string;
  createdAt: string;
  currentUsage?: number;
  extraLimitBytes?: number;
  limitBytes?: number;
  lastResetDate?: string;
}

export const getUserSubscriptionHistory = async (userId: number) => {
  const response = await client.get<UserSubscriptionHistory[]>(`/admin/user/${userId}/subscription`);
  return response.data;
};

export const revokeSubscription = async (subscriptionId: number) => {
  return await client.post(`/admin/license/revoke/${subscriptionId}`);
};

export const grantExtraLimit = async (subscriptionId: number, amountBytes: number) => {
  return await client.post(`/admin/license/subscription/${subscriptionId}/extra-limit`, { amountBytes });
};

// --- Version Management ---
export interface Version {
  id: number;
  platform: string;
  type: string;
  minVersion: string;
  latestVersion: string;
  fileUrl: string;
  enabled: boolean;
}

export const getVersions = async (): Promise<Version[]> => {
  const response = await client.get<Version[]>('/admin/versions');
  return response.data;
};

export const createVersion = async (
  version: Omit<Version, 'id'>
): Promise<Version> => {
  const response = await client.post<Version>('/admin/versions', version);
  return response.data;
};

export const updateVersion = async (
  id: number,
  version: Omit<Version, 'id'>
): Promise<Version> => {
  const response = await client.put<Version>(`/admin/versions/${id}`, version);
  return response.data;
};

export const deleteVersion = async (id: number): Promise<void> => {
  await client.delete(`/admin/versions/${id}`);
};

export interface Partnership {
  id: number;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  type: string;
  message: string;
  createdAt: string;
}

export const getPartnerships = async (page: number = 0, size: number = 20) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString()
  });
  const response = await client.get<Page<Partnership>>(`/admin/contact/partnership?${params.toString()}`);
  return response.data;
};
// --- Application Management ---

export interface Application {
  id: number;
  type: 'MANAGER' | 'AGENT';
  platform: 'WINDOWS' | 'ANDROID';
  version: string;
  originalFileName: string;
  createdAt: string;
}

export const uploadApplication = async (file: File, type: string, platform: string, version: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  formData.append('platform', platform);
  formData.append('version', version);

  const response = await client.post<Application>('/admin/apps', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getAppHistory = async (type: string, platform: string) => {
  const response = await client.get<Application[]>('/admin/apps', {
    params: { type, platform }
  });
  return response.data;
};

export const deleteApplication = async (id: number) => {
  return await client.delete(`/admin/apps/${id}`);
};

// --- Notice Management ---

export interface Notice {
  id: number;
  title: string;
  content: string;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

export const getNotices = async (page: number = 0, size: number = 10) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: 'createdAt,desc'
  });
  const response = await client.get<Page<Notice>>(`/v1/notices?${params.toString()}`);
  return response.data;
};

export const createNotice = async (data: Pick<Notice, 'title' | 'content' | 'isVisible'>) => {
  const response = await client.post<Notice>('/v1/notices', data);
  return response.data;
};

export const updateNotice = async (id: number, data: Pick<Notice, 'title' | 'content' | 'isVisible'>) => {
  const response = await client.put<Notice>(`/v1/notices/${id}`, data);
  return response.data;
};

export const deleteNotice = async (id: number) => {
  return await client.delete(`/v1/notices/${id}`);
};

// --- FAQ Management ---

export interface FaqCategory {
  id: number;
  name: string;
  displayOrder: number;
}

export interface Faq {
  id: number;
  category: FaqCategory;
  question: string;
  answer: string;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

export const getFaqs = async (page: number = 0, size: number = 10) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: 'createdAt,desc'
  });
  const response = await client.get<Page<Faq>>(`/v1/faqs?${params.toString()}`);
  return response.data;
};

export const createFaq = async (data: Pick<FaqCategory, any> | any) => { // Type loose for development
  const response = await client.post<Faq>('/v1/faqs', data);
  return response.data;
};

export const updateFaq = async (id: number, data: any) => {
  const response = await client.put<Faq>(`/v1/faqs/${id}`, data);
  return response.data;
};

export const deleteFaq = async (id: number) => {
  return await client.delete(`/v1/faqs/${id}`);
};

export const getFaqCategories = async () => {
  const response = await client.get<FaqCategory[]>('/v1/faq-categories');
  return response.data;
};

export const createFaqCategory = async (data: { name: string; displayOrder: number }) => {
  const response = await client.post<FaqCategory>('/v1/faq-categories', data);
  return response.data;
};

export const updateFaqCategory = async (id: number, data: { name: string; displayOrder: number }) => {
  const response = await client.put<FaqCategory>(`/v1/faq-categories/${id}`, data);
  return response.data;
};

export const deleteFaqCategory = async (id: number) => {
  return await client.delete(`/v1/faq-categories/${id}`);
};
